import React, { useEffect, useState } from "react";
import "./admin-dashboard.css";
import BackgroundPaths from "../../../../ui/molecules/background-paths/BackgroundPaths";
import RadarChart from "../../../../ui/molecules/radar-chart/RadarChart";
import {
  LuBookCopy,
  LuBookMarked,
  LuBookOpenText,
  LuSchool,
} from "react-icons/lu";
import { GiDuration, GiArchiveRegister } from "react-icons/gi";
import { TbSchool } from "react-icons/tb";
import { FaUsersGear } from "react-icons/fa6";
import { FaFemale, FaMale } from "react-icons/fa";
import { BsFolderCheck } from "react-icons/bs";
import { useSelector } from "react-redux";
import { onGetAdminDashboard } from "../../services/user";
import Loading from "../../../../ui/atoms/loading/Loading";
import {
  wait,
  convertDuration,
  capitalizeAll,
} from "../../../../core/utils/helpers";
import moment from "moment";

import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const AdminDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [chartVariables, setChartVariables] = useState([]);

  //
  const initConnectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser,
  );
  const _sys_role = initConnectedUser?.sys_role;
  const _inscription_id = initConnectedUser?.inscription;

  const userInformations = useSelector(
    (state) => state.setInitConf.userInformations,
  );

  useEffect(() => {
    const fetchData = async () => {
      await wait(1500);

      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      const localData = {
        inscription_id: _inscription_id,
      };

      onGetAdminDashboard(axiosPrivate, localData, signal).then((response) => {
        const _data = response?.data?.result;
        setDashboardData(_data);
        setChartData(_data?.channel_data || []);
        //
        const _chartVariables = Array.from(
          _data?.uniques_channels || [],
          (item, _) => ({ key: item, label: capitalizeAll(item) }),
        );
        setChartVariables(_chartVariables);
        //
      });
      setIsLoading(false);

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    };

    fetchData();
  }, []);

  return (
    <div className="admin-dashboard">
      {isLoading ? (
        <Loading type={"type-2"} />
      ) : (
        <React.Fragment>
          <div className="w-full section-banner">
            <div className="s-b-inner">
              <BackgroundPaths />
            </div>
            <div className="s-b-outer">
              <h1>
                <span className="mr-xs">👋🏼</span>Hi {userInformations?.firstname || "User"}, Welcome In...
              </h1>
              <p>
                Your last activity held on{" "}
                {moment(dashboardData?.before_last_login).format("LLLL")}
              </p>
            </div>
          </div>
          <div className="section-1">
            <div className="box-container">
              <div className="box-main">
                <LuBookCopy className="icon" />
                <span>Total Courses</span>
                <div className="box-main-row">
                  <p>{dashboardData?.total_courses}</p>/
                  <span>
                    {dashboardData?.total_courses_activated} activated
                  </span>
                </div>
              </div>
              <div className="box-wrapper">
                <div className="box">
                  <LuBookMarked className="icon" />
                  <div className="col">
                    <span>Total Chapters</span>
                    <p>{dashboardData?.total_chapters}</p>
                    <h3>{dashboardData?.total_chapters_activated} activated</h3>
                  </div>
                </div>
                <div className="box">
                  <LuBookOpenText className="icon" />
                  <div className="col">
                    <span>Total Lessons</span>
                    <p>{dashboardData?.total_lessons}</p>
                    <h3>{dashboardData?.total_lessons_activated} activated</h3>
                  </div>
                </div>
                <div className="box">
                  <GiDuration className="icon" />
                  <div className="col">
                    <span>Total Duration</span>
                    <p className="tick-1">
                      {convertDuration(dashboardData?.total_duration)}
                    </p>
                    <h3 className="tick-2">
                      Act. Dur.:{" "}
                      {convertDuration(dashboardData?.total_duration_activated)}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="school-container">
              <div className="item-wrapper">
                <div className="icon-wrapper">
                  <LuSchool className="icon" />
                </div>
                <div className="col-wrapper">
                  <span>Total registered Schools</span>
                  <p>{dashboardData?.total_schools}</p>
                  <h3>Active : {dashboardData?.total_schools_activated}</h3>
                </div>
              </div>
              <div className="item-wrapper">
                <div className="icon-wrapper">
                  <TbSchool className="icon" />
                </div>
                <div className="col-wrapper">
                  <span>Total registered Streams</span>
                  <p>{dashboardData?.total_streams}</p>
                  <h3>Active : {dashboardData?.total_streams_activated}</h3>
                </div>
              </div>
            </div>
            <div className="box-container">
              <div className="box-main">
                <FaUsersGear className="icon" />
                <span>Total Intern Users</span>
                <div className="box-main-row">
                  <p>{dashboardData?.total_users}</p>/
                  <span>{dashboardData?.total_users_activated} activated</span>
                </div>
              </div>
              <div className="box-wrapper">
                <div className="box">
                  <FaFemale className="icon" />
                  <div className="col">
                    <span>Female</span>
                    <p>{dashboardData?.total_users_females}</p>
                    <h3>
                      Active : {dashboardData?.total_users_females_activated}
                    </h3>
                  </div>
                </div>
                <div className="box">
                  <FaMale className="icon" />
                  <div className="col">
                    <span>Male</span>
                    <p>{dashboardData?.total_users_males}</p>
                    <h3>
                      Active : {dashboardData?.total_users_males_activated}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section-2">
            <div className="box-container">
              <div className="box-1">
                <GiArchiveRegister className="icon" />
                <span>Total Inscriptions</span>
                <h3>English Program :</h3>
                <p>{dashboardData?.total_en_inscriptions}</p>
                <h3>French Program :</h3>
                <p>{dashboardData?.total_fr_inscriptions}</p>
              </div>
              <div className="box-wrapper">
                <div className="box">
                  <div className="col">
                    <span>Female</span>
                    <h3>English Program :</h3>
                    <p>{dashboardData?.total_en_females_inscriptions}</p>
                    <h3>French Program :</h3>
                    <p>{dashboardData?.total_fr_females_inscriptions}</p>
                  </div>
                  <FaFemale className="icon" />
                </div>
                <div className="box">
                  <div className="col">
                    <span>Male</span>
                    <h3>English Program :</h3>
                    <p>{dashboardData?.total_en_males_inscriptions}</p>
                    <h3>French Program :</h3>
                    <p>{dashboardData?.total_fr_males_inscriptions}</p>
                  </div>
                  <FaMale className="icon" />
                </div>
              </div>
            </div>
            <div className="chart-container">
              <RadarChart
                data={chartData}
                variables={chartVariables}
                width={370}
                height={370}
                maxValue={10} // Max value for the domain (scores are 0-10)
              />
            </div>
            <div className="box-container">
              <div className="box box-1">
                <BsFolderCheck className="icon" />
                <span>Total Admissions</span>
                <h3>English Program :</h3>
                <p>{dashboardData?.total_en_admitted_inscriptions}</p>
                <h3>French Program :</h3>
                <p>{dashboardData?.total_fr_admitted_inscriptions}</p>
              </div>
              <div className="box-wrapper">
                <div className="box">
                  <div className="col">
                    <span>Female</span>
                    <h3>English Program :</h3>
                    <p>
                      {dashboardData?.total_en_females_admitted_inscriptions}
                    </p>
                    <h3>French Program :</h3>
                    <p>
                      {dashboardData?.total_fr_females_admitted_inscriptions}
                    </p>
                  </div>
                  <FaFemale className="icon" />
                </div>
                <div className="box">
                  <div className="col">
                    <span>Male</span>
                    <h3>English Program :</h3>
                    <p>{dashboardData?.total_en_males_admitted_inscriptions}</p>
                    <h3>French Program :</h3>
                    <p>{dashboardData?.total_fr_males_admitted_inscriptions}</p>
                  </div>
                  <FaMale className="icon" />
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default AdminDashboard;
